// AssoSpeedBusiness.jsx — Phase E : Speed Business intelligent (rotations anti-doublons)
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Plus, Loader2, ArrowLeft, Zap, Users, Clock, Trash2, Play, Square, RefreshCw,
  Download, UserPlus, Sparkles, X, Maximize2, ChevronRight, ChevronLeft, Trophy, Eye,
  Copy, Link2, Check, Calendar, MapPin,
} from 'lucide-react';

export default function AssoSpeedBusiness() {
  const { sessionId } = useParams();
  if (sessionId) return <SessionDetail sessionId={sessionId} />;
  return <SessionsList />;
}

// ───────────────────────────────────────────────────────────────────────────
// LIST VIEW
// ───────────────────────────────────────────────────────────────────────────
function SessionsList() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/speed-business');
      setList(data);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur de chargement');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="page-container">
      <div className="page-header flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="label-uppercase mb-2 flex items-center gap-1.5" style={{ color: 'var(--theme-primary)' }}>
            <Zap className="w-3 h-3" /> Networking
          </div>
          <h1 className="page-title">Speed Business</h1>
          <p className="page-subtitle">Sessions de rotation intelligentes pour maximiser les rencontres uniques entre vos membres.</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary inline-flex items-center gap-2" data-testid="speed-create-btn">
          <Plus className="w-4 h-4" /> Nouvelle session
        </button>
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" /></div>
      ) : list.length === 0 ? (
        <div className="card text-center py-12" data-testid="speed-empty">
          <Zap className="w-10 h-10 mx-auto mb-3 text-[#94A3B8]" />
          <h3 className="font-display text-lg font-bold text-[#0F172A] mb-1">Aucune session Speed Business</h3>
          <p className="text-sm text-[#475569] mb-4 max-w-md mx-auto">
            Créez votre première session pour générer un planning de rotations optimisé : chaque participant rencontre
            un maximum de nouvelles personnes, sans doublons.
          </p>
          <button onClick={() => setCreating(true)} className="btn-primary inline-flex items-center gap-2" data-testid="speed-create-empty">
            <Plus className="w-4 h-4" /> Créer une session
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="speed-list">
          {list.map((s) => (
            <button key={s.id} onClick={() => navigate(`/dashboard/speed-business/${s.id}`)} className="card text-left hover:shadow-md transition-shadow" data-testid={`speed-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <h3 className="font-display font-bold text-[#0F172A] text-base truncate flex-1">{s.nom}</h3>
                <StatutBadge statut={s.statut} />
              </div>
              {s.description && <p className="text-xs text-[#475569] line-clamp-2 mb-3">{s.description}</p>}
              <div className="grid grid-cols-3 gap-2 text-xs">
                <Metric label="Participants" value={s.nb_participants || 0} />
                <Metric label="Rotations" value={s.nb_rotations} />
                <Metric label="Table" value={s.taille_table} />
              </div>
            </button>
          ))}
        </div>
      )}

      {creating && <CreateSessionModal onClose={() => setCreating(false)} onSuccess={(id) => { setCreating(false); load(); navigate(`/dashboard/speed-business/${id}`); }} />}
    </div>
  );
}

function StatutBadge({ statut }) {
  const map = {
    brouillon: { label: 'Brouillon', cls: 'bg-[#F1F5F9] text-[#475569]' },
    planifiee: { label: 'Planifiée', cls: 'bg-amber-100 text-amber-700' },
    en_cours: { label: 'En cours', cls: 'bg-emerald-100 text-emerald-700' },
    terminee: { label: 'Terminée', cls: 'bg-[#E2E8F0] text-[#475569]' },
  };
  const s = map[statut] || map.brouillon;
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${s.cls}`} data-testid={`speed-statut-${statut}`}>{s.label}</span>;
}

function Metric({ label, value }) {
  return (
    <div className="text-center p-2 bg-[#F9F9F8] rounded-lg">
      <div className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#94A3B8] mb-0.5">{label}</div>
      <div className="font-display text-lg font-black text-[#0F172A]">{value}</div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// CREATE MODAL
// ───────────────────────────────────────────────────────────────────────────
function CreateSessionModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ nom: '', description: '', date_session: '', lieu: '', taille_table: 4, nb_rotations: 6, duree_rotation_min: 5 });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/speed-business', form);
      toast.success('Session créée');
      onSuccess(data.id);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#0F172A]/70 backdrop-blur-sm grid place-items-center p-3 sm:p-6 fade-in" onClick={onClose} data-testid="speed-create-modal">
      <form onSubmit={submit} className="card max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-[#0F172A] flex items-center gap-2"><Zap className="w-5 h-5" style={{ color: 'var(--theme-primary)' }} /> Nouvelle session</h3>
          <button type="button" onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]" aria-label="Fermer"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-uppercase block mb-1.5">Nom de la session</label>
            <input type="text" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Ex: Speed Business Q1 2026" className="input-base" data-testid="speed-form-nom" />
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Description (optionnelle)</label>
            <textarea rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Contexte, format…" className="input-base" data-testid="speed-form-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-uppercase block mb-1.5">Date (texte libre)</label>
              <input type="text" value={form.date_session} onChange={(e) => setForm({ ...form, date_session: e.target.value })} placeholder="Ex: Jeudi 3 septembre 2026" className="input-base" data-testid="speed-form-date" />
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Lieu</label>
              <input type="text" value={form.lieu} onChange={(e) => setForm({ ...form, lieu: e.target.value })} placeholder="Ex: Le Sphinx, Figeac" className="input-base" data-testid="speed-form-lieu" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label-uppercase block mb-1.5">Taille table</label>
              <input type="number" min="2" max="8" value={form.taille_table} onChange={(e) => setForm({ ...form, taille_table: +e.target.value })} className="input-base" data-testid="speed-form-table" />
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Rotations</label>
              <input type="number" min="1" max="20" value={form.nb_rotations} onChange={(e) => setForm({ ...form, nb_rotations: +e.target.value })} className="input-base" data-testid="speed-form-rotations" />
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Durée (min)</label>
              <input type="number" min="1" max="30" value={form.duree_rotation_min} onChange={(e) => setForm({ ...form, duree_rotation_min: +e.target.value })} className="input-base" data-testid="speed-form-duree" />
            </div>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-[#F1F5F9]">
          <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="speed-form-submit">{saving ? 'Création…' : 'Créer la session'}</button>
        </div>
      </form>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// DETAIL VIEW
// ───────────────────────────────────────────────────────────────────────────
function SessionDetail({ sessionId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [presentationMode, setPresentationMode] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/speed-business/${sessionId}`);
      setData(data);
    } catch (err) {
      toast.error('Session introuvable'); navigate('/dashboard/speed-business');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [sessionId]);

  if (loading || !data) {
    return <div className="page-container"><div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" /></div></div>;
  }

  const action = async (fn, msg) => {
    setBusy(true);
    try {
      const result = await fn();
      // Si la réponse contient un document session complet, on l'utilise directement
      if (result?.data && result.data.id === sessionId) {
        setData((prev) => ({ ...prev, ...result.data, participants: prev?.participants || [] }));
      }
      await load();
      if (msg) toast.success(msg);
    } catch (err) { toast.error(err?.response?.data?.error || 'Erreur'); }
    finally { setBusy(false); }
  };

  const generer = () => action(() => api.post(`/speed-business/${sessionId}/generer`), 'Planning généré !');
  const importMembres = () => action(() => api.post(`/speed-business/${sessionId}/import-membres`).then((r) => {
    if (r.data.ajoutes === 0) toast.info(r.data.message || 'Aucun nouveau participant');
    return r;
  }), 'Membres importés');
  const start = () => action(() => api.post(`/speed-business/${sessionId}/start`), 'Session démarrée');
  const finish = () => { if (!confirm('Terminer cette session ?')) return; action(() => api.post(`/speed-business/${sessionId}/finish`), 'Session terminée'); };
  const removeParticipant = (pid) => { if (!confirm('Retirer ce participant ?')) return; action(() => api.delete(`/speed-business/${sessionId}/participants/${pid}`), 'Participant retiré'); };
  const deleteSession = () => {
    if (!confirm('Supprimer définitivement cette session ?')) return;
    api.delete(`/speed-business/${sessionId}`).then(() => { toast.success('Session supprimée'); navigate('/dashboard/speed-business'); }).catch((err) => toast.error(err?.response?.data?.error || 'Erreur'));
  };

  const ouvrirInscriptions = () => action(() => api.post(`/speed-business/${sessionId}/inscription/ouvrir`), 'Inscriptions ouvertes');
  const fermerInscriptions = () => action(() => api.post(`/speed-business/${sessionId}/inscription/fermer`), 'Inscriptions fermées');
  const inscriptionUrl = data.inscription_token ? `${window.location.origin}/speed/${data.inscription_token}` : '';
  const copyLink = () => {
    if (!inscriptionUrl) return;
    navigator.clipboard?.writeText(inscriptionUrl);
    setCopied(true);
    toast.success('Lien copié !');
    setTimeout(() => setCopied(false), 2000);
  };

  if (presentationMode) return <PresentationView data={data} onClose={() => setPresentationMode(false)} />;

  return (
    <div className="page-container">
      <div className="page-header">
        <Link to="/dashboard/speed-business" className="inline-flex items-center gap-1 text-sm text-[#475569] hover:text-[#0F172A] mb-4" data-testid="speed-back"><ArrowLeft className="w-4 h-4" /> Toutes les sessions</Link>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
              <StatutBadge statut={data.statut} />
            </div>
            <h1 className="page-title">{data.nom}</h1>
            {data.description && <p className="page-subtitle">{data.description}</p>}
          </div>
          <div className="flex gap-2 flex-wrap">
            {data.planning && <button onClick={() => setPresentationMode(true)} className="btn-ghost inline-flex items-center gap-1.5" data-testid="speed-present-btn"><Maximize2 className="w-4 h-4" /> Présentation</button>}
            {data.statut === 'planifiee' && <button onClick={start} disabled={busy} className="btn-primary inline-flex items-center gap-1.5" data-testid="speed-start-btn"><Play className="w-4 h-4" /> Démarrer</button>}
            {data.statut === 'en_cours' && <button onClick={finish} disabled={busy} className="btn-secondary inline-flex items-center gap-1.5" data-testid="speed-finish-btn"><Square className="w-4 h-4" /> Terminer</button>}
            <button onClick={deleteSession} disabled={busy} className="btn-ghost !text-rose-600 inline-flex items-center gap-1.5" data-testid="speed-delete-btn"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {/* Stats config + couverture */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="speed-kpis">
        <KpiCard label="Participants" value={data.participants?.length || 0} />
        <KpiCard label="Taille table" value={data.taille_table} />
        <KpiCard label="Rotations" value={data.nb_rotations} />
        <KpiCard label="Durée" value={`${data.duree_rotation_min} min`} />
        <KpiCard label="Couverture" value={data.stats ? `${data.stats.couverture_pct}%` : '—'} accent />
      </div>

      {/* INSCRIPTIONS PUBLIQUES */}
      <div className="card !p-5 mb-6" style={{ borderWidth: '1px', borderColor: data.inscription_ouverte ? 'var(--theme-primary)' : '#E5E7EB' }} data-testid="speed-inscription-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h3 className="font-display font-bold text-[#0F172A] flex items-center gap-2"><Link2 className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} /> Lien d&apos;inscription public</h3>
            <p className="text-sm text-[#475569] mt-1 max-w-xl">Partagez ce lien : chaque inscription (adhérent ou invité) devient automatiquement un participant de la session.</p>
          </div>
          {data.inscription_ouverte
            ? <button onClick={fermerInscriptions} disabled={busy} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid="speed-inscription-close">Fermer les inscriptions</button>
            : <button onClick={ouvrirInscriptions} disabled={busy} className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5" data-testid="speed-inscription-open"><Link2 className="w-3.5 h-3.5" /> Ouvrir les inscriptions</button>}
        </div>
        {data.inscription_ouverte && inscriptionUrl && (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <div className="flex-1 min-w-[220px] font-mono text-xs text-[#334155] bg-[#F9F9F8] border border-[#E2E8F0] rounded-lg px-3 py-2.5 truncate" data-testid="speed-inscription-url">{inscriptionUrl}</div>
            <button onClick={copyLink} className="btn-primary !py-2 !px-3 text-xs inline-flex items-center gap-1.5" data-testid="speed-inscription-copy">
              {copied ? <><Check className="w-3.5 h-3.5" /> Copié</> : <><Copy className="w-3.5 h-3.5" /> Copier le lien</>}
            </button>
            <a href={inscriptionUrl} target="_blank" rel="noreferrer" className="btn-ghost !py-2 !px-3 text-xs inline-flex items-center gap-1.5" data-testid="speed-inscription-preview"><Eye className="w-3.5 h-3.5" /> Aperçu</a>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* PARTICIPANTS */}
        <div className="card !p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-[#0F172A] flex items-center gap-2"><Users className="w-4 h-4" /> Participants <span className="text-sm text-[#94A3B8] font-normal">({data.participants?.length || 0})</span></h3>
            <div className="flex gap-1.5">
              <button onClick={importMembres} disabled={busy} className="btn-ghost !py-1.5 !px-2.5 text-xs inline-flex items-center gap-1" data-testid="speed-import-membres" title="Importer tous les membres actifs"><UserPlus className="w-3.5 h-3.5" /> Membres</button>
              <ImportEventButton sessionId={sessionId} onSuccess={load} />
              <AddParticipantButton sessionId={sessionId} onSuccess={load} />
            </div>
          </div>
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto" data-testid="speed-participants-list">
            {(data.participants || []).length === 0 ? (
              <p className="text-sm text-[#475569] italic py-8 text-center">Aucun participant pour le moment. Importez vos membres ou ajoutez manuellement.</p>
            ) : (
              data.participants.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 p-2.5 bg-[#F9F9F8] rounded-lg text-sm group" data-testid={`speed-participant-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#0F172A] truncate flex items-center gap-1.5">
                      {p.prenom} {p.nom}
                      {p.type && <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${p.type === 'adherent' ? 'bg-[#DBEAFE] text-[#1D4ED8]' : 'bg-[#FEF3C7] text-[#92400E]'}`}>{p.type === 'adherent' ? 'Adhérent' : 'Invité'}</span>}
                    </div>
                    {(p.entreprise || p.fonction) && <div className="text-xs text-[#475569] truncate">{[p.fonction, p.entreprise].filter(Boolean).join(' · ')}</div>}
                  </div>
                  <button onClick={() => removeParticipant(p.id)} className="text-[#94A3B8] hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity p-1" aria-label="Retirer" data-testid={`speed-remove-${p.id}`}><X className="w-3.5 h-3.5" /></button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* PLANNING */}
        <div className="card !p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-[#0F172A] flex items-center gap-2"><Sparkles className="w-4 h-4" /> Planning rotations</h3>
            <button onClick={generer} disabled={busy || (data.participants?.length || 0) < 2} className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid="speed-generate-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} {data.planning ? 'Régénérer' : 'Générer'}
            </button>
          </div>
          {!data.planning ? (
            <p className="text-sm text-[#475569] italic py-8 text-center">
              Cliquez sur <strong>Générer</strong> après avoir ajouté vos participants.
              L&apos;algorithme va répartir les tables en minimisant les doublons de rencontres.
            </p>
          ) : (
            <>
              {data.stats && <CouvertureBar stats={data.stats} />}
              <div className="space-y-3 max-h-[400px] overflow-y-auto" data-testid="speed-planning-list">
                {data.planning.map((rot) => (
                  <RotationCard key={rot.rotation} rotation={rot} participants={data.participants} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent }) {
  return (
    <div className={`card !p-3 text-center ${accent ? '' : ''}`} style={accent ? { borderColor: 'var(--theme-primary)', borderWidth: '2px' } : {}}>
      <div className="label-uppercase mb-1">{label}</div>
      <div className="font-display text-xl font-black" style={accent ? { color: 'var(--theme-primary)' } : { color: '#0F172A' }}>{value}</div>
    </div>
  );
}

function CouvertureBar({ stats }) {
  return (
    <div className="mb-4 p-3 bg-[#F9F9F8] rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold text-[#0F172A] flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> Couverture des paires</div>
        <div className="text-xs tabular-nums"><strong className="text-[#0F172A]">{stats.paires_uniques_rencontrees}</strong> <span className="text-[#94A3B8]">/ {stats.paires_total_possibles}</span> <span className="font-bold" style={{ color: 'var(--theme-primary)' }}>· {stats.couverture_pct}%</span></div>
      </div>
      <div className="w-full h-2 bg-[#E2E8F0] rounded-full overflow-hidden"><div className="h-full transition-all" style={{ width: `${stats.couverture_pct}%`, background: 'var(--theme-primary)' }} /></div>
      {stats.doublons > 0 && <div className="text-[10px] text-[#94A3B8] mt-1.5">{stats.doublons} doublon{stats.doublons > 1 ? 's' : ''} sur {stats.paires_realisees_brutes} paires planifiées</div>}
    </div>
  );
}

function RotationCard({ rotation, participants }) {
  const byId = Object.fromEntries((participants || []).map((p) => [p.id, p]));
  return (
    <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={`speed-rotation-${rotation.rotation}`}>
      <div className="px-3 py-2 bg-[#F9F9F8] flex items-center justify-between">
        <div className="font-bold text-sm text-[#0F172A]">Rotation {rotation.rotation}</div>
        <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">{rotation.tables.length} table{rotation.tables.length > 1 ? 's' : ''} {rotation.doublons > 0 && <span className="text-rose-500 ml-1">· {rotation.doublons} doublon{rotation.doublons > 1 ? 's' : ''}</span>}</div>
      </div>
      <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {rotation.tables.map((t) => (
          <div key={t.table_num} className="p-2 bg-white border border-[#E2E8F0] rounded text-xs" data-testid={`speed-table-${rotation.rotation}-${t.table_num}`}>
            <div className="font-bold text-[#0F172A] mb-1">Table {t.table_num}</div>
            <ul className="space-y-0.5 text-[#475569]">
              {t.participant_ids.map((pid) => {
                const p = byId[pid];
                return <li key={pid} className="truncate">{p ? `${p.prenom} ${p.nom}` : pid}</li>;
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// ADD PARTICIPANT (manuel)
// ───────────────────────────────────────────────────────────────────────────
function AddParticipantButton({ sessionId, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ prenom: '', nom: '', entreprise: '', fonction: '', email: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.prenom && !form.nom && !form.email) { toast.error('Au moins un champ est requis'); return; }
    setSaving(true);
    try {
      await api.post(`/speed-business/${sessionId}/participants`, form);
      toast.success('Participant ajouté');
      setForm({ prenom: '', nom: '', entreprise: '', fonction: '', email: '' });
      setOpen(false);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="btn-primary !py-1.5 !px-2.5 text-xs inline-flex items-center gap-1" data-testid="speed-add-participant"><Plus className="w-3.5 h-3.5" /> Ajouter</button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-[#0F172A]/70 backdrop-blur-sm grid place-items-center p-3 sm:p-6 fade-in" onClick={() => setOpen(false)} data-testid="speed-add-modal">
          <form onSubmit={submit} className="card max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold text-[#0F172A] flex items-center gap-2"><UserPlus className="w-5 h-5" /> Ajouter un participant</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="text" placeholder="Prénom" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} className="input-base" data-testid="speed-add-prenom" />
              <input type="text" placeholder="Nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="input-base" data-testid="speed-add-nom" />
              <input type="text" placeholder="Entreprise" value={form.entreprise} onChange={(e) => setForm({ ...form, entreprise: e.target.value })} className="input-base col-span-2" data-testid="speed-add-entreprise" />
              <input type="text" placeholder="Fonction" value={form.fonction} onChange={(e) => setForm({ ...form, fonction: e.target.value })} className="input-base col-span-2" data-testid="speed-add-fonction" />
              <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-base col-span-2" data-testid="speed-add-email" />
            </div>
            <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-[#F1F5F9]">
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary">Annuler</button>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="speed-add-submit">{saving ? 'Ajout…' : 'Ajouter'}</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// IMPORT FROM EVENT (presences ou billets)
// ───────────────────────────────────────────────────────────────────────────
function ImportEventButton({ sessionId, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [evtId, setEvtId] = useState('');
  const [source, setSource] = useState('presences');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/evenements').then(({ data }) => setEvents(data)).catch(() => {});
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!evtId) { toast.error('Sélectionnez un événement'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/speed-business/${sessionId}/import-event`, { evenement_id: evtId, source });
      if (data.ajoutes === 0) toast.info(data.message || 'Aucun nouveau participant');
      else toast.success(`${data.ajoutes} participant${data.ajoutes > 1 ? 's' : ''} importé${data.ajoutes > 1 ? 's' : ''}`);
      setOpen(false); onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="btn-ghost !py-1.5 !px-2.5 text-xs inline-flex items-center gap-1" data-testid="speed-import-event" title="Importer depuis un événement"><Download className="w-3.5 h-3.5" /> Event</button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-[#0F172A]/70 backdrop-blur-sm grid place-items-center p-3 sm:p-6 fade-in" onClick={() => setOpen(false)} data-testid="speed-import-modal">
          <form onSubmit={submit} className="card max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold text-[#0F172A] flex items-center gap-2"><Download className="w-5 h-5" /> Importer depuis un événement</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label-uppercase block mb-1.5">Événement</label>
                <select value={evtId} onChange={(e) => setEvtId(e.target.value)} className="input-base" data-testid="speed-import-event-select">
                  <option value="">— Sélectionner —</option>
                  {events.map((e) => <option key={e.id} value={e.id}>{e.nom} ({new Date(e.date_debut).toLocaleDateString()})</option>)}
                </select>
              </div>
              <div>
                <label className="label-uppercase block mb-1.5">Source</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setSource('presences')} className={`flex-1 py-2 text-xs font-bold rounded-lg border-2 ${source === 'presences' ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]' : 'border-[#E2E8F0] text-[#475569]'}`} data-testid="speed-source-presences">Présents (émargement)</button>
                  <button type="button" onClick={() => setSource('billets')} className={`flex-1 py-2 text-xs font-bold rounded-lg border-2 ${source === 'billets' ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]' : 'border-[#E2E8F0] text-[#475569]'}`} data-testid="speed-source-billets">Billetterie</button>
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-[#F1F5F9]">
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary">Annuler</button>
              <button type="submit" disabled={busy} className="btn-primary" data-testid="speed-import-submit">{busy ? 'Import…' : 'Importer'}</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// PRESENTATION MODE (plein écran pour animer la session)
// ───────────────────────────────────────────────────────────────────────────
function PresentationView({ data, onClose }) {
  const [idx, setIdx] = useState(0);
  const [timeLeft, setTimeLeft] = useState((data.duree_rotation_min || 5) * 60);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setTimeLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [running]);

  useEffect(() => { setTimeLeft((data.duree_rotation_min || 5) * 60); }, [idx, data.duree_rotation_min]);

  const rotation = data.planning?.[idx];
  if (!rotation) return null;

  const byId = Object.fromEntries((data.participants || []).map((p) => [p.id, p]));
  const mm = Math.floor(timeLeft / 60).toString().padStart(2, '0');
  const ss = (timeLeft % 60).toString().padStart(2, '0');
  const expired = timeLeft === 0;

  return (
    <div className="fixed inset-0 z-[80] bg-[#0F172A] text-white p-6 sm:p-10 overflow-y-auto" data-testid="speed-presentation">
      <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white" aria-label="Fermer" data-testid="speed-present-close"><X className="w-6 h-6" /></button>

      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 font-bold mb-1">Speed Business · {data.nom}</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black">Rotation {rotation.rotation} <span className="text-white/30 text-3xl">/ {data.planning.length}</span></h2>
          </div>
          <div className={`text-6xl sm:text-7xl font-display font-black tabular-nums ${expired ? 'text-rose-400 animate-pulse' : ''}`} style={!expired ? { color: 'var(--theme-primary)' } : {}} data-testid="speed-timer">
            {mm}:{ss}
          </div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          <button onClick={() => setRunning((r) => !r)} className="px-5 py-2 rounded-full font-bold text-sm flex items-center gap-2" style={{ background: 'var(--theme-primary)', color: '#0F172A' }} data-testid="speed-timer-toggle">
            {running ? <><Square className="w-4 h-4" /> Pause</> : <><Play className="w-4 h-4" /> Démarrer</>}
          </button>
          <button onClick={() => setTimeLeft((data.duree_rotation_min || 5) * 60)} className="px-5 py-2 rounded-full border border-white/20 hover:bg-white/10 font-bold text-sm flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Reset</button>
          <button onClick={() => { setIdx((i) => Math.max(0, i - 1)); setRunning(false); }} disabled={idx === 0} className="px-4 py-2 rounded-full border border-white/20 hover:bg-white/10 disabled:opacity-30" data-testid="speed-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => { setIdx((i) => Math.min(data.planning.length - 1, i + 1)); setRunning(false); }} disabled={idx === data.planning.length - 1} className="px-4 py-2 rounded-full border border-white/20 hover:bg-white/10 disabled:opacity-30" data-testid="speed-next"><ChevronRight className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="speed-present-tables">
          {rotation.tables.map((t) => (
            <div key={t.table_num} className="bg-white/5 backdrop-blur rounded-2xl p-5 border border-white/10">
              <div className="text-xs uppercase tracking-[0.2em] text-white/40 font-bold mb-2">Table</div>
              <div className="font-display text-4xl font-black mb-3" style={{ color: 'var(--theme-primary)' }}>{t.table_num}</div>
              <ul className="space-y-1.5">
                {t.participant_ids.map((pid) => {
                  const p = byId[pid];
                  return (
                    <li key={pid} className="text-sm">
                      <div className="font-semibold">{p ? `${p.prenom} ${p.nom}` : pid}</div>
                      {p?.entreprise && <div className="text-xs text-white/50">{p.entreprise}</div>}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
